import { useCallback, useEffect, useRef, useState } from "react";
import {
  hasAdminView,
  useAdminViewRestoring,
  useAdminViewState,
} from "../../lib/admin/adminViewState";
import { Link } from "react-router-dom";
import { apiFetch } from "../../api/client";

/**
 * Входящие обращения: просьбы «дайте ещё заданий» из виджета SQL
 * и сообщения об ошибках, отправленные с доски.
 */

type InboxKind = "task_request" | "bug_report";
type InboxStatus = "new" | "done";

type InboxItem = {
  id: string;
  kind: InboxKind;
  status: InboxStatus;
  message: string;
  board_id: string | null;
  board_url: string | null;
  author_label: string | null;
  author_email: string | null;
  context: Record<string, unknown>;
  created_at: string;
  handled_at: string | null;
};

const KIND_LABELS: Record<InboxKind, string> = {
  task_request: "Ещё задания",
  bug_report: "Ошибка",
};

const STATUS_FILTERS = [
  { id: "new", label: "Новые" },
  { id: "all", label: "Все" },
  { id: "done", label: "Разобранные" },
] as const;

const KIND_FILTERS = [
  { id: "all", label: "Любые" },
  { id: "task_request", label: "Задания" },
  { id: "bug_report", label: "Ошибки" },
] as const;

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Короткая сводка контекста: база, номер задания, сколько уже решено. */
function contextSummary(item: InboxItem): string {
  const c = item.context ?? {};
  const parts: string[] = [];
  if (typeof c.datasetTitle === "string" && c.datasetTitle) parts.push(c.datasetTitle);
  if (typeof c.taskNumber === "number") parts.push(`задание ${c.taskNumber}`);
  if (typeof c.solvedCount === "number") parts.push(`решено ${c.solvedCount}`);
  if (typeof c.reportKind === "string" && c.reportKind) parts.push(c.reportKind);
  if (typeof c.techSummary === "string" && c.techSummary) parts.push(c.techSummary);
  return parts.join(" · ");
}

export function AdminInboxTab() {
  // Фильтры и список переживают уход на доску и «Назад»; список тихо обновляется.
  const restored = useAdminViewRestoring() && hasAdminView("admin:inbox:items");
  const [items, setItems] = useAdminViewState<InboxItem[]>("admin:inbox:items", []);
  const [newCount, setNewCount] = useAdminViewState("admin:inbox:newCount", 0);
  const [status, setStatus] = useAdminViewState<(typeof STATUS_FILTERS)[number]["id"]>(
    "admin:inbox:status",
    "new"
  );
  const [kind, setKind] = useAdminViewState<(typeof KIND_FILTERS)[number]["id"]>(
    "admin:inbox:kind",
    "all"
  );
  const [err, setErr] = useState("");
  const [firstLoad, setFirstLoad] = useState(!restored);
  const [busyId, setBusyId] = useState("");
  const aliveRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ items: InboxItem[]; newCount: number }>(
        `/api/v1/admin/inbox?status=${status}&kind=${kind}&limit=200`
      );
      if (!aliveRef.current) return;
      setItems(data.items ?? []);
      setNewCount(data.newCount ?? 0);
      setErr("");
    } catch (e) {
      if (!aliveRef.current) return;
      setErr(e instanceof Error ? e.message : "Не удалось загрузить обращения");
    } finally {
      if (aliveRef.current) setFirstLoad(false);
    }
  }, [status, kind]);

  useEffect(() => {
    aliveRef.current = true;
    void load();
    return () => {
      aliveRef.current = false;
    };
  }, [load]);

  const setItemStatus = useCallback(
    async (id: string, next: InboxStatus) => {
      setBusyId(id);
      try {
        await apiFetch(`/api/v1/admin/inbox/${id}`, {
          method: "PATCH",
          json: { status: next },
        });
        await load();
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Не удалось изменить статус");
      } finally {
        setBusyId("");
      }
    },
    [load]
  );

  return (
    <div className="wb-admin-users">
      <div className="wb-admin-users__head">
        <h2 className="wb-admin-users__title">
          Обращения <span className="wb-admin-users__count">новых: {newCount}</span>
        </h2>
      </div>
      <p className="wb-cabinet-plan-hint__text">
        Сюда приходят просьбы добавить задания из виджета «SQL» на доске и сообщения об
        ошибках, отправленные через «Сообщить об ошибке». Письма на почту продолжают уходить
        как раньше — это копия, чтобы ничего не потерялось.
      </p>

      <div className="wb-admin-users__head" style={{ gap: 8, flexWrap: "wrap" }}>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={
              status === f.id ? "wb-cabinet-btn wb-cabinet-btn--primary" : "wb-cabinet-btn"
            }
            onClick={() => setStatus(f.id)}
          >
            {f.label}
          </button>
        ))}
        <span style={{ width: 12 }} />
        {KIND_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={kind === f.id ? "wb-cabinet-btn wb-cabinet-btn--primary" : "wb-cabinet-btn"}
            onClick={() => setKind(f.id)}
          >
            {f.label}
          </button>
        ))}
        <button type="button" className="wb-cabinet-btn" onClick={() => void load()}>
          Обновить
        </button>
      </div>

      {err ? (
        <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
          {err}
        </p>
      ) : null}

      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <th>Когда</th>
              <th>Тип</th>
              <th>Кто</th>
              <th>Сообщение</th>
              <th>Где</th>
              <th>Что делать</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{formatDate(item.created_at)}</td>
                <td>{KIND_LABELS[item.kind] ?? item.kind}</td>
                <td>{item.author_label || item.author_email || "гость"}</td>
                <td style={{ whiteSpace: "pre-wrap", maxWidth: 480 }}>
                  {item.message}
                  {contextSummary(item) ? (
                    <div style={{ opacity: 0.65, fontSize: 12, marginTop: 4 }}>
                      {contextSummary(item)}
                    </div>
                  ) : null}
                </td>
                <td>
                  {item.board_id ? (
                    <Link to={`/board/${item.board_id}?spectate=1`} title="Открыть доску">
                      доска
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {item.status === "new" ? (
                    <button
                      type="button"
                      className="wb-cabinet-btn"
                      disabled={busyId === item.id}
                      onClick={() => void setItemStatus(item.id, "done")}
                    >
                      Разобрано
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="wb-cabinet-btn"
                      disabled={busyId === item.id}
                      onClick={() => void setItemStatus(item.id, "new")}
                    >
                      Вернуть в новые
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!items.length && !firstLoad ? (
              <tr>
                <td colSpan={6}>Пока пусто</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
